import { faSort } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useAuth,
  useOrganization,
  useTeam,
  useTeamDefaultTemplates,
  useOrgDefaultTemplates,
} from "@hooks";
import { useState, useEffect, useEffectEvent } from "react";
import {
  useNavigate,
  useSearchParams,
  useParams,
} from "react-router-dom";

import { UpdateTeamDefaultButton } from "../autotemplates/UpdateTeamDefaultButton";

export const DefaultTemplatesTable = () => {
  const { teamUuid } = useParams();
  const { userUuid } = useAuth();
  const { isOrgAdmin } = useOrganization();
  const { teamByUuid } = useTeam();

  const team = teamByUuid(teamUuid);

  // Fetch team and org defaults based on URL param, not context
  const {
    defaultTemplates: teamDefaults,
    orgUuid: orgUuidForTeam,
    refetch: refetchTeamDefaults,
  } = useTeamDefaultTemplates(teamUuid);

  const {
    defaultTemplates: orgDefaults,
    refetch: refetchOrgDefaults,
  } = useOrgDefaultTemplates(orgUuidForTeam);

  const teamDefaultTemplates = (() => {
    const templateMap = new Map();

    orgDefaults.forEach((template) => {
      templateMap.set(template.uuid, template);
    });

    teamDefaults.forEach((template) => {
      templateMap.set(template.uuid, template);
    });

    return Array.from(templateMap.values());
  })();

  const isCurrentUserAnAdmin =
    team?.users?.some((u) => u.uuid === userUuid && u.isAdmin) ||
    isOrgAdmin;
  const scrollPositionParamName = "templateScrollPosition";

  const [currentSort, setCurrentSort] = useState("name");
  const [isSortReverse, setIsSortReverse] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const onOrgUuidChange = useEffectEvent(() => {
    if (refetchTeamDefaults) {
      refetchTeamDefaults();
    }
    if (orgUuidForTeam && refetchOrgDefaults) {
      refetchOrgDefaults();
    }
  });

  useEffect(() => {
    onOrgUuidChange();
  }, [orgUuidForTeam]);

  // Restore scroll position from URL params
  useEffect(() => {
    const scrollPosition = searchParams.get(scrollPositionParamName);

    if (scrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, Number.parseInt(scrollPosition, 10));
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete(scrollPositionParamName);
        setSearchParams(newSearchParams, { replace: true });
      }, 0);
    }
  }, [searchParams, setSearchParams, scrollPositionParamName]);

  const sortTemplates = (templates, sortBy, reverse) => {
    return [...templates].sort((a, b) => {
      let aValue, bValue;

      if (sortBy === "owner") {
        aValue = `${a.user?.firstName || ""} ${
          a.user?.lastName || ""
        }`.toLowerCase();
        bValue = `${b.user?.firstName || ""} ${
          b.user?.lastName || ""
        }`.toLowerCase();
      } else if (sortBy === "name") {
        aValue = a.name?.toLowerCase() || "";
        bValue = b.name?.toLowerCase() || "";
      } else if (sortBy === "templateType") {
        aValue = a.templateType || "";
        bValue = b.templateType || "";
      } else {
        return 0;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return reverse ? bValue - aValue : aValue - bValue;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return reverse
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      }

      return 0;
    });
  };

  const sortedTemplates = (() => {
    if (currentSort) {
      return sortTemplates(
        teamDefaultTemplates,
        currentSort,
        isSortReverse,
      );
    }
    return teamDefaultTemplates;
  })();

  const handleSort = (sortBy) => {
    setIsSortReverse((prev) => !prev);
    setCurrentSort(sortBy);
  };

  const handleRefresh = () => {
    refetchTeamDefaults();
    if (orgUuidForTeam) {
      refetchOrgDefaults();
    }
  };

  if (teamDefaultTemplates.length === 0) {
    return (
      <div className="bg-secondary border-2 border-dashed border-primary rounded-standard py-10 px-sm w-full my-md">
        <h5 className="text-center text-secondary">
          No team default templates set yet.
        </h5>
        <p className="text-center text-sm text-secondary mt-sm">
          Team admins can set defaults from the template edit page.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-secondary border border-primary rounded-large py-sm px-sm w-full my-md">
      <table className="table-auto w-full text-primary text-left">
        <thead>
          <tr className="border-b border-primary">
            <th
              className="font-semibold text-sm px-sm py-sm cursor-pointer"
              onClick={() => handleSort("name")}
            >
              Template Name
              <FontAwesomeIcon
                icon={faSort}
                size="xs"
                className="ml-1"
              />
            </th>
            <th
              className="font-semibold text-sm px-sm py-sm cursor-pointer"
              onClick={() => handleSort("templateType")}
            >
              Type
              <FontAwesomeIcon
                icon={faSort}
                size="xs"
                className="ml-1"
              />
            </th>
            <th
              className="font-semibold text-sm px-sm py-sm cursor-pointer"
              onClick={() => handleSort("owner")}
            >
              Owner
              <FontAwesomeIcon
                icon={faSort}
                size="xs"
                className="ml-1"
              />
            </th>
            {isCurrentUserAnAdmin && (
              <th className="font-semibold text-sm px-sm py-sm text-center" />
            )}
          </tr>
        </thead>
        <tbody>
          {sortedTemplates.map((template) => (
            <tr
              key={template.uuid}
              onClick={() => {
                const scrollPosition = window.scrollY;
                const returnPath = `/teams/${teamUuid}`;
                const params = new URLSearchParams({
                  returnTo: `${returnPath}?${scrollPositionParamName}=${scrollPosition}`,
                });

                navigate(
                  `/templates/shared/${
                    template.uuid
                  }?${params.toString()}`,
                );
              }}
              className="cursor-pointer even:bg-transparent odd:bg-primary hover:bg-brand-subtle rounded-standard"
            >
              <td className="py-sm px-sm">
                <h5 className="text-sm">{template.name}</h5>
                {template.description && (
                  <p className="text-xs text-secondary mt-1">
                    {template.description}
                  </p>
                )}
              </td>
              <td className="py-sm px-sm">
                <span className="text-sm">
                  {template.templateType === "SOAP"
                    ? "SOAP Format"
                    : "Freeform"}
                </span>
              </td>
              <td className="py-sm px-sm">
                <span className="text-sm">
                  {template.user?.firstName} {template.user?.lastName}
                </span>
              </td>
              {isCurrentUserAnAdmin && (
                <td
                  className="py-sm px-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-center">
                    <UpdateTeamDefaultButton
                      template={template}
                      onSuccess={handleRefresh}
                      source="team defaults table"
                    />
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
